import { createElement, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, Upload, User } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SettingsCard,
  SettingsPage,
  SettingsPageLoading,
  SettingsRow,
  SettingsSaveBar,
  settingsInputClass,
} from '@/components/app/settings'
import { cn } from '@/lib/utils'
import {
  getProfile,
  updateProfile,
  uploadProfilePicture,
} from '@/lib/api/features/profile'
import { OfflineFallback } from '@/components/app/offline/OfflineFallback'
import { useIsApiDown } from '@/lib/api/useIsApiDown'
import { isNetworkError } from '@/lib/api/apiHealth'

export default function ProfileSettingsPage() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const profileFormSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .email(t('profileSettings.validation.emailInvalid'))
          .min(1, t('profileSettings.validation.emailRequired'))
          .max(255, t('profileSettings.validation.emailMax')),
        name: z
          .string()
          .trim()
          .min(1, t('profileSettings.validation.nameRequired'))
          .max(100, t('profileSettings.validation.nameMax')),
      }),
    [t],
  )
  type ProfileFormData = z.infer<typeof profileFormSchema>

  // Profile picture state (separate from form)
  const [profilePicture, setProfilePicture] = useState<string>('')
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const isApiDown = useIsApiDown()

  // Form state with react-hook-form
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      email: '',
      name: '',
    },
  })

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoadingProfile(true)
        setLoadFailed(false)
        const profileData = await getProfile()

        form.reset({
          email: profileData.email,
          name: profileData.name || '',
        })

        // Set profile picture if available
        if (profileData.profilePictureUrl) {
          setProfilePicture(profileData.profilePictureUrl)
        }
      } catch (error) {
        setLoadFailed(true)
        if (!isNetworkError(error)) {
          toast.error(t('profileSettings.toast.loadFailed'))
        }
      } finally {
        setIsLoadingProfile(false)
      }
    }

    loadProfile()
  }, [form, reloadKey, t])

  const handleProfilePictureChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('profileSettings.toast.pictureSizeError'))
      return
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('profileSettings.toast.pictureTypeError'))
      return
    }

    setIsUploading(true)
    try {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfilePicture(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      const result = await uploadProfilePicture({ file })
      setProfilePicture(result.url)
      toast.success(t('profileSettings.toast.pictureUploaded'))
    } catch {
      toast.error(t('profileSettings.toast.pictureUploadFailed'))
      setProfilePicture('') // Reset preview on error
    } finally {
      setIsUploading(false)
    }
  }

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await updateProfile({
        email: data.email,
        name: data.name,
      })
      // Saved values become the new baseline, which hides the save bar.
      form.reset(data)

      toast.success(t('profileSettings.toast.updated'))
    } catch {
      toast.error(t('profileSettings.toast.updateFailed'))
    }
  }

  if (isLoadingProfile) {
    return <SettingsPageLoading />
  }

  if (loadFailed && isApiDown) {
    return (
      <SettingsPage title={t('profileSettings.title')}>
        <OfflineFallback
          description={t('profileSettings.offlineDescription')}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SettingsPage>
    )
  }

  const offlineTitle = isApiDown ? t('workspaceSettings.offlineTitle') : undefined
  const nameError = form.formState.errors.name?.message

  return (
    <SettingsPage
      title={t('profileSettings.title')}
      description={t('profileSettings.description')}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
        <SettingsCard>
          {/* Profile picture — uploads immediately, outside the save bar */}
          <SettingsRow
            label={t('profileSettings.fields.picture')}
            description={t('profileSettings.fields.pictureHint')}
          >
            <div className="flex items-center gap-2">
              <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary">
                {isUploading ? (
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                ) : profilePicture ? (
                  <img
                    src={profilePicture}
                    alt={t('profileSettings.fields.pictureAlt')}
                    className="size-full object-cover"
                  />
                ) : (
                  <User className="size-4 text-muted-foreground" />
                )}
              </div>
              <label
                htmlFor="profile-picture"
                title={offlineTitle}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-lg border bg-card px-3 text-sm font-medium shadow-xs transition-colors',
                  isUploading || isApiDown
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-accent',
                )}
              >
                <Upload className="size-3.5" />
                {t('profileSettings.fields.upload')}
              </label>
              <input
                id="profile-picture"
                type="file"
                accept="image/*"
                aria-label={t('profileSettings.fields.uploadAria')}
                className="sr-only"
                onChange={handleProfilePictureChange}
                disabled={isUploading || isApiDown}
              />
            </div>
          </SettingsRow>

          {/* Email — shown for reference; not editable here */}
          <SettingsRow
            label={t('profileSettings.fields.email')}
            description={t('profileSettings.fields.emailHint')}
            htmlFor="profile-email"
            control="field"
            error={form.formState.errors.email?.message}
          >
            <input
              id="profile-email"
              type="email"
              {...form.register('email')}
              disabled
              className={cn(settingsInputClass, 'bg-secondary text-muted-foreground')}
            />
          </SettingsRow>

          {/* Name */}
          <SettingsRow
            label={t('profileSettings.fields.name')}
            htmlFor="profile-name"
            control="field"
            error={nameError}
          >
            <input
              id="profile-name"
              {...form.register('name')}
              placeholder={t('profileSettings.fields.namePlaceholder')}
              aria-invalid={nameError ? true : undefined}
              className={settingsInputClass}
            />
          </SettingsRow>
        </SettingsCard>

        <SettingsSaveBar
          visible={form.formState.isDirty}
          isSaving={form.formState.isSubmitting}
          saveDisabled={isApiDown}
          saveTitle={offlineTitle}
          onDiscard={() => form.reset()}
        />
      </form>
    </SettingsPage>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/',
)({
  component: ProfileSettingsPage,
})
